import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, TrendingUp, TrendingDown, MessageSquare, X, ChevronRight, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Filter } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Treemap } from 'recharts';
// Land filter options
const regions = [{
  id: 'konzern',
  name: 'Alle Länder',
  icon: Building2
}, {
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'usa',
  name: 'USA'
}, {
  id: 'china',
  name: 'China'
}, {
  id: 'uk',
  name: 'Großbritannien'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'australien',
  name: 'Australien'
}, {
  id: 'japan',
  name: 'Japan'
}];
// Mock data per land
const regionData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  konzern: {
    positive: 12450,
    neutral: 4230,
    negative: 1820,
    total: 18500,
    avgRating: 4.2,
    trend: 0.15
  },
  deutschland: {
    positive: 3820,
    neutral: 1180,
    negative: 510,
    total: 5510,
    avgRating: 4.3,
    trend: 0.18
  },
  usa: {
    positive: 2650,
    neutral: 890,
    negative: 380,
    total: 3920,
    avgRating: 4.1,
    trend: 0.12
  },
  china: {
    positive: 3100,
    neutral: 1050,
    negative: 420,
    total: 4570,
    avgRating: 4.0,
    trend: 0.08
  },
  uk: {
    positive: 1050,
    neutral: 380,
    negative: 160,
    total: 1590,
    avgRating: 4.2,
    trend: 0.14
  },
  frankreich: {
    positive: 820,
    neutral: 310,
    negative: 140,
    total: 1270,
    avgRating: 4.1,
    trend: 0.1
  },
  australien: {
    positive: 620,
    neutral: 230,
    negative: 90,
    total: 940,
    avgRating: 4.4,
    trend: 0.22
  },
  japan: {
    positive: 390,
    neutral: 190,
    negative: 120,
    total: 700,
    avgRating: 3.9,
    trend: 0.05
  }
};
// Topic clusters data
const topicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    market: string;
    date: string;
    rating: number;
  }>;
}>> = {
  konzern: [{
    name: 'Fahrzeugqualität',
    size: 4200,
    sentiment: 'positive',
    comments: [{
      text: 'Das Fahrzeug überzeugt auf ganzer Linie – Verarbeitung und Fahrgefühl sind erstklassig!',
      market: 'BMW Niederlassung Hamburg',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Mein neuer BMW 5er ist ein Traum. Qualität auf höchstem Niveau.',
      market: 'BMW Niederlassung München',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'The build quality is outstanding – every detail is perfect.',
      market: 'BMW of Manhattan, New York',
      date: '2024-01-26',
      rating: 5
    }, {
      text: 'Bestes Fahrzeug das ich je gefahren bin – BMW überzeugt.',
      market: 'BMW Niederlassung Frankfurt',
      date: '2024-01-25',
      rating: 5
    }, {
      text: 'Technisch auf dem neuesten Stand, Fahrwerk und Motor perfekt abgestimmt.',
      market: 'Autohaus Gebhardt Erlangen',
      date: '2024-01-24',
      rating: 4
    }]
  }, {
    name: 'Beratungsqualität',
    size: 3100,
    sentiment: 'positive',
    comments: [{
      text: 'Der Berater hat sich sehr viel Zeit genommen und alle Fragen kompetent beantwortet.',
      market: 'BMW Niederlassung Berlin',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Excellent consultation – no pressure, felt very well looked after.',
      market: 'BMW Park Lane, London',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Sehr professionelle Beratung, alle Optionen wurden erklärt.',
      market: 'BMW Niederlassung Hannover',
      date: '2024-01-26',
      rating: 4
    }]
  }, {
    name: 'Wartezeiten Service',
    size: 2800,
    sentiment: 'negative',
    comments: [{
      text: 'Werkstatttermin erst in 3 Wochen – das ist zu lang für ein Premium-Fahrzeug.',
      market: 'Autohaus Welzel Berlin',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Service appointment took 4 weeks – unacceptable for a premium brand.',
      market: 'BMW of Beverly Hills',
      date: '2024-01-27',
      rating: 1
    }, {
      text: 'Bitte mehr Kapazitäten in der Werkstatt bereitstellen!',
      market: 'Autohaus Becker Dortmund',
      date: '2024-01-26',
      rating: 2
    }, {
      text: 'Wartezeit von 4 Wochen auf Ersatzteile ist nicht akzeptabel.',
      market: 'BMW Niederlassung Duisburg',
      date: '2024-01-25',
      rating: 2
    }]
  }, {
    name: 'Probefahrt',
    size: 2400,
    sentiment: 'positive',
    comments: [{
      text: 'Probefahrt war ein Erlebnis – sofort verliebt in den BMW X5!',
      market: 'BMW Niederlassung München',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Test drive was amazing – no pressure to buy, great experience.',
      market: 'BMW Sydney Rushcutters Bay',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Preisgestaltung',
    size: 2100,
    sentiment: 'neutral',
    comments: [{
      text: 'Preise sind hoch, aber für die Qualität gerechtfertigt.',
      market: 'BMW Niederlassung Leipzig',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Pricing is premium but the quality justifies it.',
      market: 'BMW Tokyo Aoyama',
      date: '2024-01-27',
      rating: 4
    }, {
      text: 'Etwas teurer als Mitbewerber, aber die Qualität stimmt.',
      market: 'BMW Niederlassung Bremen',
      date: '2024-01-26',
      rating: 3
    }]
  }, {
    name: 'Sauberkeit & Ambiente',
    size: 1800,
    sentiment: 'positive',
    comments: [{
      text: 'Showroom ist immer makellos sauber und modern eingerichtet.',
      market: 'BMW Niederlassung Karlsruhe',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beautiful showroom – feels like a premium experience from the moment you walk in.',
      market: 'BMW Paris Champs-Élysées',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Parkplatz',
    size: 1500,
    sentiment: 'negative',
    comments: [{
      text: 'Parkplatzsituation vor dem Autohaus ist katastrophal.',
      market: 'BMW Niederlassung Berlin-Mitte',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Parking is very limited, especially on weekends.',
      market: 'BMW Center Beijing',
      date: '2024-01-27',
      rating: 3
    }]
  }, {
    name: 'Lieferzeiten',
    size: 1200,
    sentiment: 'neutral',
    comments: [{
      text: 'Lieferzeit von 6 Monaten ist lang, aber das Warten hat sich gelohnt.',
      market: 'BMW Niederlassung Essen',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Delivery took longer than expected but communication was good.',
      market: 'BMW of Manhattan',
      date: '2024-01-27',
      rating: 3
    }]
  }],
  deutschland: [{
    name: 'Fahrzeugqualität',
    size: 1620,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Beratungsqualität',
    size: 1280,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Probefahrt',
    size: 980,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Wartezeiten Service',
    size: 880,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Lieferzeiten',
    size: 750,
    sentiment: 'neutral',
    comments: []
  }],
  usa: [{
    name: 'Vehicle Quality',
    size: 1100,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Sales Consultation',
    size: 890,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Service Wait Times',
    size: 720,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Test Drive',
    size: 580,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Pricing',
    size: 630,
    sentiment: 'neutral',
    comments: []
  }],
  china: [{
    name: '车辆质量',
    size: 1350,
    sentiment: 'positive',
    comments: []
  }, {
    name: '销售咨询',
    size: 980,
    sentiment: 'positive',
    comments: []
  }, {
    name: '等待时间',
    size: 820,
    sentiment: 'negative',
    comments: []
  }, {
    name: '试驾体验',
    size: 760,
    sentiment: 'positive',
    comments: []
  }, {
    name: '价格',
    size: 660,
    sentiment: 'neutral',
    comments: []
  }]
};
// 12-month trend data
const monthlyTrendData: Record<string, Array<{
  month: string;
  rating: number;
  reviews: number;
}>> = {
  konzern: [{
    month: 'Feb 23',
    rating: 4.05,
    reviews: 14200
  }, {
    month: 'Mär 23',
    rating: 4.08,
    reviews: 15100
  }, {
    month: 'Apr 23',
    rating: 4.06,
    reviews: 14800
  }, {
    month: 'Mai 23',
    rating: 4.1,
    reviews: 15500
  }, {
    month: 'Jun 23',
    rating: 4.12,
    reviews: 16200
  }, {
    month: 'Jul 23',
    rating: 4.09,
    reviews: 15800
  }, {
    month: 'Aug 23',
    rating: 4.11,
    reviews: 16000
  }, {
    month: 'Sep 23',
    rating: 4.14,
    reviews: 16500
  }, {
    month: 'Okt 23',
    rating: 4.13,
    reviews: 17200
  }, {
    month: 'Nov 23',
    rating: 4.16,
    reviews: 17800
  }, {
    month: 'Dez 23',
    rating: 4.18,
    reviews: 18200
  }, {
    month: 'Jan 24',
    rating: 4.2,
    reviews: 18500
  }],
  deutschland: [{
    month: 'Feb 23',
    rating: 4.12,
    reviews: 4200
  }, {
    month: 'Mär 23',
    rating: 4.15,
    reviews: 4400
  }, {
    month: 'Apr 23',
    rating: 4.18,
    reviews: 4350
  }, {
    month: 'Mai 23',
    rating: 4.2,
    reviews: 4600
  }, {
    month: 'Jun 23',
    rating: 4.22,
    reviews: 4800
  }, {
    month: 'Jul 23',
    rating: 4.24,
    reviews: 4700
  }, {
    month: 'Aug 23',
    rating: 4.26,
    reviews: 4900
  }, {
    month: 'Sep 23',
    rating: 4.25,
    reviews: 5000
  }, {
    month: 'Okt 23',
    rating: 4.27,
    reviews: 5100
  }, {
    month: 'Nov 23',
    rating: 4.28,
    reviews: 5200
  }, {
    month: 'Dez 23',
    rating: 4.3,
    reviews: 5350
  }, {
    month: 'Jan 24',
    rating: 4.3,
    reviews: 5510
  }],
  usa: [{
    month: 'Feb 23',
    rating: 3.95,
    reviews: 3100
  }, {
    month: 'Mär 23',
    rating: 3.98,
    reviews: 3200
  }, {
    month: 'Apr 23',
    rating: 3.96,
    reviews: 3150
  }, {
    month: 'Mai 23',
    rating: 4.0,
    reviews: 3300
  }, {
    month: 'Jun 23',
    rating: 4.02,
    reviews: 3450
  }, {
    month: 'Jul 23',
    rating: 4.0,
    reviews: 3400
  }, {
    month: 'Aug 23',
    rating: 4.03,
    reviews: 3500
  }, {
    month: 'Sep 23',
    rating: 4.05,
    reviews: 3600
  }, {
    month: 'Okt 23',
    rating: 4.06,
    reviews: 3700
  }, {
    month: 'Nov 23',
    rating: 4.08,
    reviews: 3800
  }, {
    month: 'Dez 23',
    rating: 4.1,
    reviews: 3900
  }, {
    month: 'Jan 24',
    rating: 4.1,
    reviews: 3920
  }]
};
// Simple custom treemap component that doesn't rely on Recharts content prop
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  // Calculate total for percentages
  const total = data.reduce((sum, item) => sum + item.size, 0);
  // Sort by size descending
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  // Color mapping
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-789">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      const percentage = (item.size / total * 100).toFixed(0);
      // Determine grid span based on size
      let colSpan = 1;
      let rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-790">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-791">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-792">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
export function KonzernStimmungPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [selectedTopic, setSelectedTopic] = useState<(typeof topicClusters)['konzern'][0] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const currentData = regionData[selectedRegion] || regionData.konzern;
  const currentTopics = topicClusters[selectedRegion] || topicClusters.konzern;
  const currentTrend = monthlyTrendData[selectedRegion] || monthlyTrendData.konzern;
  const totalSentiments = currentData.positive + currentData.neutral + currentData.negative;
  const positivePercent = Math.round(currentData.positive / totalSentiments * 100);
  const neutralPercent = Math.round(currentData.neutral / totalSentiments * 100);
  const negativePercent = Math.round(currentData.negative / totalSentiments * 100);
  const handleTopicClick = (topic: any) => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setIsDialogOpen(true);
    }
  };
  const treemapData = currentTopics.map(topic => ({
    name: topic.name,
    size: topic.size,
    sentiment: topic.sentiment
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-793">
      {/* Land Filter */}
      <Card className="!p-4" data-id="element-794">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-795">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-796">
            <Filter size={16} data-id="element-797" />
            <span data-id="element-798">Land:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-799">
            {regions.map(region => {
            const isSelected = selectedRegion === region.id;
            const Icon = region.icon;
            return <button key={region.id} onClick={() => setSelectedRegion(region.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-[#1C69D4] text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-800">
                  {Icon && <Icon size={14} data-id="element-801" />}
                  {region.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-802">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} change={currentData.trend > 0 ? `+${currentData.trend.toFixed(2)}` : currentData.trend.toFixed(2)} trend={currentData.trend >= 0 ? 'up' : 'down'} icon={<TrendingUp size={18} data-id="element-804" />} subtext="vs. Vormonat" data-id="element-803" />
        <KPICard title="Gesamtbewertungen" value={currentData.total.toLocaleString('de-DE')} change="+8%" trend="up" icon={<MessageSquare size={18} data-id="element-806" />} subtext="diese Periode" data-id="element-805" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} change={`${currentData.positive.toLocaleString('de-DE')}`} trend="up" icon={<ThumbsUp size={18} data-id="element-808" />} subtext="Kommentare" data-id="element-807" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} change={`${currentData.negative.toLocaleString('de-DE')}`} trend="down" icon={<ThumbsDown size={18} data-id="element-810" />} subtext="Kommentare" data-id="element-809" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-811">
        {/* Absolute Resonanz */}
        <Card data-id="element-812">
          <div className="p-6" data-id="element-813">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-814">
              Absolute Resonanz
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-815">
              Verteilung der Sentiment-Analyse
            </p>

            {/* Stacked Bar */}
            <div className="mb-6" data-id="element-816">
              <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-817">
                <div style={{
                width: `${positivePercent}%`,
                backgroundColor: '#639754'
              }} className="flex items-center justify-center" data-id="element-818">
                  {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-819">
                      {positivePercent}%
                    </span>}
                </div>
                <div style={{
                width: `${neutralPercent}%`,
                backgroundColor: '#1C69D4'
              }} className="flex items-center justify-center" data-id="element-820">
                  {neutralPercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-821">
                      {neutralPercent}%
                    </span>}
                </div>
                <div style={{
                width: `${negativePercent}%`,
                backgroundColor: '#E03C32'
              }} className="flex items-center justify-center" data-id="element-822">
                  {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-823">
                      {negativePercent}%
                    </span>}
                </div>
              </div>
            </div>

            {/* Legend with numbers */}
            <div className="space-y-3" data-id="element-824">
              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-825">
                <div className="flex items-center gap-3" data-id="element-826">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#639754'
                }} data-id="element-827">
                    <ThumbsUp size={18} className="text-white" data-id="element-828" />
                  </div>
                  <div data-id="element-829">
                    <div className="font-semibold text-gray-900" data-id="element-830">Positiv</div>
                    <div className="text-xs text-gray-500" data-id="element-831">
                      Zufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-832">
                  <div className="text-2xl font-bold" style={{
                  color: '#639754'
                }} data-id="element-833">
                    {currentData.positive.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-834">
                    {positivePercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#dbeafe'
            }} data-id="element-835">
                <div className="flex items-center gap-3" data-id="element-836">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#1C69D4'
                }} data-id="element-837">
                    <Minus size={18} className="text-white" data-id="element-838" />
                  </div>
                  <div data-id="element-839">
                    <div className="font-semibold text-gray-900" data-id="element-840">Neutral</div>
                    <div className="text-xs text-gray-500" data-id="element-841">
                      Gemischte Meinungen
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-842">
                  <div className="text-2xl font-bold" style={{
                  color: '#1C69D4'
                }} data-id="element-843">
                    {currentData.neutral.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-844">
                    {neutralPercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-845">
                <div className="flex items-center gap-3" data-id="element-846">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-847">
                    <ThumbsDown size={18} className="text-white" data-id="element-848" />
                  </div>
                  <div data-id="element-849">
                    <div className="font-semibold text-gray-900" data-id="element-850">Negativ</div>
                    <div className="text-xs text-gray-500" data-id="element-851">
                      Unzufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-852">
                  <div className="text-2xl font-bold" style={{
                  color: '#E03C32'
                }} data-id="element-853">
                    {currentData.negative.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-854">
                    {negativePercent}% aller Bewertungen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Themen Cluster */}
        <Card data-id="element-855">
          <div className="p-6" data-id="element-856">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-857">
              Themen-Cluster
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-858">
              Häufig erwähnte Themen in Bewertungen (klicken für Details)
            </p>

            <div className="h-80" data-id="element-859">
              <SimpleTreemap data={treemapData} onClick={handleTopicClick} data-id="element-860" />
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-861">
              <div className="flex items-center gap-2" data-id="element-862">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#639754'
              }} data-id="element-863"></div>
                <span className="text-xs text-gray-600" data-id="element-864">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-865">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-866"></div>
                <span className="text-xs text-gray-600" data-id="element-867">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-868">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-869"></div>
                <span className="text-xs text-gray-600" data-id="element-870">Negativ</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 12-Month Trend Chart */}
      <Card data-id="element-871">
        <div className="p-6" data-id="element-872">
          <div className="flex items-center justify-between mb-6" data-id="element-873">
            <div data-id="element-874">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-875">
                Reputationsentwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-876">
                Durchschnittliche Bewertung der letzten 12 Monate
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-877">
              <div className="flex items-center gap-2" data-id="element-878">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-879"></div>
                <span className="text-gray-600" data-id="element-880">Ø Bewertung</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-881">
            <ResponsiveContainer width="100%" height="100%" data-id="element-882">
              <LineChart data={currentTrend} data-id="element-883">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-884" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-885" />
                <YAxis domain={[3.8, 4.6]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => value.toFixed(1)} data-id="element-886" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [value.toFixed(2), name === 'rating' ? 'Ø Bewertung' : 'Bewertungen']} data-id="element-887" />
                <Line type="monotone" dataKey="rating" stroke="#1C69D4" strokeWidth={3} dot={{
                fill: '#1C69D4',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#0653B6',
                stroke: '#1C69D4',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-888" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Trend Summary */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between" data-id="element-889">
            <div className="flex items-center gap-4" data-id="element-890">
              <div className="flex items-center gap-2" data-id="element-891">
                <TrendingUp size={18} style={{
                color: '#639754'
              }} data-id="element-892" />
                <span className="text-sm text-gray-600" data-id="element-893">
                  <span className="font-semibold" style={{
                  color: '#639754'
                }} data-id="element-894">
                    +
                    {((currentTrend[11].rating - currentTrend[0].rating) * 100 / currentTrend[0].rating).toFixed(1)}
                    %
                  </span>{' '}
                  Verbesserung im Jahresvergleich
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-500" data-id="element-895">
              Aktuell:{' '}
              <span className="font-semibold text-gray-900" data-id="element-896">
                {currentTrend[11].rating.toFixed(2)}
              </span>{' '}
              Sterne
            </div>
          </div>
        </div>
      </Card>

      {/* Topic Detail Dialog */}
      <AnimatePresence data-id="element-897">
        {isDialogOpen && selectedTopic && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-898">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-899">
              {/* Dialog Header */}
              <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-900">
                <div className="flex items-center justify-between" data-id="element-901">
                  <div data-id="element-902">
                    <div className="flex items-center gap-3" data-id="element-903">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-904">
                        {selectedTopic.name}
                      </h3>
                      <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-905">
                        {selectedTopic.sentiment === 'positive' ? 'Positiv' : selectedTopic.sentiment === 'negative' ? 'Negativ' : 'Neutral'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-906">
                      {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                      in Kundenbewertungen
                    </p>
                  </div>
                  <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-907">
                    <X size={20} data-id="element-908" />
                  </button>
                </div>
              </div>

              {/* Dialog Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-909">
                <div className="space-y-4" data-id="element-910">
                  {selectedTopic.comments.map((comment, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-911">
                      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-912">
                        <div className="flex items-center gap-2" data-id="element-913">
                          <MapPin size={14} style={{
                      color: '#1C69D4'
                    }} data-id="element-914" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-915">
                            {comment.market}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-916">
                          <div className="flex" data-id="element-917">
                            {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-918">
                                ★
                              </span>)}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-919">
                            <Calendar size={12} data-id="element-920" />
                            {new Date(comment.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic" data-id="element-921">
                        "{comment.text}"
                      </p>
                    </motion.div>)}
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-922">
                <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-923">
                  Schließen
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}